import { iconLabel } from '~/client/src/shared/components/IconPicker/iconCatalog'
import {
  COMPANY_LOGOS_GROUP,
  iconGroupOf,
} from '~/client/src/shared/components/IconPicker/iconGroups'
import MapViewLocationIcon, {
  LOCATION_ICONS,
} from '~/client/src/shared/enums/SitemapAttributeIcon'
import {
  type PictogramIconName,
  PICTOGRAMS,
  type PictogramSlug,
  pictogramSlugOf,
  toPictogramIconName,
} from '~/client/src/shared/icons/pictograms'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import {
  INFORMATION_POINT_KINDS,
  type InformationPointGroupId,
  type InformationPointKind,
  informationPointGroupOf,
  isInformationPointKind,
} from '~/client/src/shared/resources/domain/informationPointGroups'
import {
  kindsOfIcon,
  pictogramsOfKind,
} from '~/client/src/shared/resources/domain/locationKind'
import { compareLabelsAlphanumeric } from '~/client/src/shared/utils/compareGroupsByLabel'

/** Logos belong to no type: placed from here they are a General Information Point. */
const LOGO_KIND: InformationPointKind = 'general_information_point'

// Both tabs group by the Information Point groups: General first, the rest by name, logos last.
const GENERAL_GROUP: InformationPointGroupId = 'general'
const pinnedRank = (group: string) =>
  group === GENERAL_GROUP ? -1 : group === COMPANY_LOGOS_GROUP ? 1 : 0

export const sortPickerGroups = (a: string, b: string): number =>
  pinnedRank(a) - pinnedRank(b) ||
  compareLabelsAlphanumeric(pickerGroupLabel(a), pickerGroupLabel(b))

const byLabel =
  <T>(labelOf: (item: T) => string) =>
  (a: T, b: T): number =>
    compareLabelsAlphanumeric(labelOf(a), labelOf(b))

export function pickerGroupLabel(group: string): string {
  const t = Localization.translator
  return group === COMPANY_LOGOS_GROUP
    ? t.iconPicker.companyLogos
    : t.informationPointGroups[group as InformationPointGroupId]
}

export interface ITypeItem {
  id: InformationPointKind
  group: string
  icon: PictogramSlug
}

export interface IIconItem {
  /** The icon name a location stores: a StruxHub glyph, or `pictogram:<slug>`. */
  id: MapViewLocationIcon | PictogramIconName
  group: string
  kind: InformationPointKind
}

const isLogoItem = (item: IIconItem) => item.group === COMPANY_LOGOS_GROUP

const matches = (needle: string, ...labels: string[]) =>
  labels.some(label => label.toLocaleLowerCase().includes(needle))

/**
 * One row per type the project can create, drawn with its default icon. A query matches type
 * names only.
 */
export function typeItems(
  query: string,
  isCreatable: (kind: string) => boolean,
): ITypeItem[] {
  const t = Localization.translator
  const needle = query.trim().toLocaleLowerCase()

  return INFORMATION_POINT_KINDS.filter(
    kind => isCreatable(kind) && matches(needle, t.informationPointTypes[kind]),
  )
    .map(kind => ({
      id: kind,
      group: informationPointGroupOf(kind),
      icon: pictogramsOfKind(kind)[0],
    }))
    .sort(byLabel(item => t.informationPointTypes[item.id]))
}

/**
 * Every icon an Information Point can be placed with, once, under the group of the first type
 * it belongs to that the project can create, which is also the type it is placed as; an icon
 * whose types the project cannot create is left out. The StruxHub glyphs first, then the
 * library signs, each by name (company logos in catalog order), as the Library lists them. A
 * query matches the icon or any of its types; found by a type, the icon is listed and placed as
 * that type.
 */
export function iconItems(
  query: string,
  isCreatable: (kind: string) => boolean,
): IIconItem[] {
  const t = Localization.translator
  const needle = query.trim().toLocaleLowerCase()
  const iconNames: (MapViewLocationIcon | PictogramIconName)[] = [
    ...LOCATION_ICONS,
    ...PICTOGRAMS.map(({ slug }) => toPictogramIconName(slug)),
  ]

  return iconNames
    .flatMap((iconName): IIconItem[] => {
      const isLogo = iconGroupOf(iconName) === COMPANY_LOGOS_GROUP
      const ownKinds = (
        isLogo
          ? [LOGO_KIND]
          : kindsOfIcon(iconName).filter(isInformationPointKind)
      ).filter(isCreatable)
      const kind = matches(needle, iconLabel(iconName))
        ? ownKinds[0]
        : ownKinds.find(own => matches(needle, t.informationPointTypes[own]))

      return kind
        ? [
            {
              id: iconName,
              group: isLogo
                ? COMPANY_LOGOS_GROUP
                : informationPointGroupOf(kind),
              kind,
            },
          ]
        : []
    })
    .sort(
      (a, b) =>
        Number(!!pictogramSlugOf(a.id)) - Number(!!pictogramSlugOf(b.id)) ||
        Number(isLogoItem(a)) - Number(isLogoItem(b)) ||
        (isLogoItem(a)
          ? 0
          : compareLabelsAlphanumeric(iconLabel(a.id), iconLabel(b.id))),
    )
}
